<template>
  <div class="AdditionalCoveragesForm">
    <SummaryCoverages
      v-if="price && showSummary"
      :price="price"
      :answers="summaryAnswers"
      :period="period"
      @submit="hireCoverages"
    />

    <AddCoveragesForm
      v-else
      :answers="repricingAnswers"
      @submit="repricing"
    />

    <div class="actions">
      <BaseButton
        v-if="price && showSummary"
        type="outlined"
        icon="icon-arrow-left"
        @click="showSummary = false"
        :name="gtmPrefix + 'summary_' + BACK_BTN"
        :id="gtmPrefix + 'summary_' + BACK_BTN"
        :emitGTMEvent="{ event: GTMEventType.BACK }"
      >
        {{ t('rater.base.back') }}
      </BaseButton>

      <BaseButton
        v-else
        type="outlined"
        icon="icon-arrow-left"
        @click="emit('previous')"
        :name="gtmPrefix + 'previous_' + BACK_BTN"
        :id="gtmPrefix + 'previous_' + BACK_BTN"
        :emitGTMEvent="{ event: GTMEventType.BACK }"
      >
        {{ t('rater.base.back') }}
      </BaseButton>

      <BaseButton
        type="transparent"
        @click="emit('skip-coverages')"
      >
        {{ t('hiring.base.previous-price') }}
      </BaseButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed } from 'vue'
  import { useForeignHealthStore } from '@/modules/foreign-health/hiring/stores'
  import AddCoveragesForm from '../coverages/AddCoveragesForm.vue'
  import SummaryCoverages from '../coverages/SummaryCoverages.vue'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import ForeignHealthHiringAnswers from '@/modules/foreign-health/hiring/models/APP/ForeignHealthHiringAnswers'
  import type { PricePeriodType } from '@/modules/common/common/models/APP/types'
  import ForeignHealthRaterAnswers from '@/modules/foreign-health/rater/models/APP/ForeignHealthRaterAnswers'
  import { priceFormat } from '@/utils/textUtils'
  import GTMEventType from '@/modules/core/models/GTMEventType'
  import { BACK_BTN } from '@/modules/core/models/IdHTMLItems'
  import { useI18n } from 'vue-i18n'
  import { useGTMStore } from '@/modules/core/stores/gtmStore'
  import SpanishBranches from '@/modules/core/models/SpanishBranches'
  import type ForeignHealthInsured from '../../models/MID/ForeignHealthInsured'
  import { ErrorDisplayTypeEnum } from '@/modules/core/models/enums'

  const { t } = useI18n()
  const foreignHealthStore = useForeignHealthStore()
  const gtmStore = useGTMStore()

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const emit = defineEmits(['previous', 'skip-coverages', 'submit-coverages'])

  const repricingAnswers = ref(new ForeignHealthHiringAnswers(props.answers))
  const showSummary = ref(false)
  const price = ref<string | undefined>(undefined)
  const branchName = ref<keyof typeof SpanishBranches | null>(null)

  const summaryAnswers = computed(() => repricingAnswers.value as ForeignHealthHiringAnswers)
  const period = computed(
    () =>
      props.configurations.billingCycles?.[repricingAnswers.value.billingCycle as PricePeriodType]
        ?.textInPrice || ''
  )

  const forWhomPeople = computed(() => repricingAnswers.value.insureds.length)

  const gtmPrefix = 'hiring_health_additional_coverages_form_'

  async function repricing(insureds: ForeignHealthInsured[]) {
    repricingAnswers.value.insureds.forEach((insured, index) => {
      insured.additionalCoverages = insureds[index].additionalCoverages
    })
    if (props.answers.coverageCode) {
      const coverageCode = props.answers.coverageCode
      repricingAnswers.value.insureds.map((insured) => {
        return {
          ...insured,
          additionalCoverages: insured.additionalCoverages.push({
            key: coverageCode,
            checked: true,
            value: null
          })
        }
      })
    }

    const product = await foreignHealthStore.getNewRaterPrice(
      repricingAnswers.value as ForeignHealthHiringAnswers,
      ErrorDisplayTypeEnum.TOAST,
      false
    )

    const billingCycle = repricingAnswers.value?.billingCycle
    const newPrice = product?.prices[billingCycle as PricePeriodType]
    const newPriceTotal = product?.totalPrices[billingCycle as PricePeriodType] ?? undefined
    price.value = priceFormat(newPrice)

    onModifyInsuranceTriggerGTMEvents(newPriceTotal)
    showSummary.value = true
  }

  function onModifyInsuranceTriggerGTMEvents(totalPrice: string | undefined) {
    branchName.value = props.configurations.businessLine
    gtmStore.sendRaterOrHiringInteractionEvent({
      event: GTMEventType.RECALCULATE,
      ramo: SpanishBranches[branchName.value!],
      num_asegurados: forWhomPeople.value ?? undefined,
      value: totalPrice
    })
  }

  async function hireCoverages() {
    const newAnswers = new ForeignHealthRaterAnswers(
      repricingAnswers.value as ForeignHealthHiringAnswers
    )
    const product = await foreignHealthStore.modifyRate(newAnswers)
    if (!product) return

    emit('submit-coverages')
  }
</script>

<style lang="scss" scoped>
  .AdditionalCoveragesForm {
    .hiring-subtitle {
      margin-bottom: $spacing-l;
      color: $color-neutral;
      font-size: $font-size-l;
      font-weight: $font-weight-400;
    }

    .v-divider {
      margin: $spacing-l 0;
    }

    .OptionCardInsuranceComponent:not(:last-child) {
      margin-bottom: $spacing-s;
    }
  }
</style>
